/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import {
  ResourceStatus,
  ResourcesFilterInput,
  ResourcesFilterableFields,
  FilterComparisonOperator,
} from "~/generated/graphql";
import { isNotEmpty, isNotNil } from "~/tools";
import { createFilterByInput } from "~/utils/createFilterByInput";
import { ResourceQuery } from "~/views/resources/types";

export function getResourceStatusIconName(status: ResourceStatus) {
  if (status === ResourceStatus.ConnectionLost) {
    return "cloudOffline";
  } else {
    return "cloud";
  }
}

export function getResourceStatusMessage(status: ResourceStatus) {
  if (status === ResourceStatus.ConnectionLost) {
    return "Lost Connection";
  } else {
    return "Online";
  }
}

export function getResourceColorContext(status: ResourceStatus) {
  if (status === ResourceStatus.ConnectionLost) {
    return "danger";
  } else {
    return "success";
  }
}

const isQueryStateDate = (queryState: ResourceQuery): boolean => {
  return (
    isNotNil(queryState.dateAddedStartUTC) &&
    isNotEmpty(queryState.dateAddedStartUTC) &&
    isNotNil(queryState.dateAddedEndUTC) &&
    isNotEmpty(queryState.dateAddedEndUTC)
  );
};

// eslint-disable-next-line complexity
export function prepareFilter(
  queryState: ResourceQuery
): ResourcesFilterInput[] | undefined {
  const sidebarFilters = [
    ...(isQueryStateDate(queryState)
      ? [
          {
            field: ResourcesFilterableFields.CreatedAt,
            operator: FilterComparisonOperator.Ge,
            values: [queryState.dateAddedStartUTC ?? ""],
          },
          {
            field: ResourcesFilterableFields.CreatedAt,
            operator: FilterComparisonOperator.Le,
            values: [queryState.dateAddedEndUTC ?? ""],
          },
        ]
      : []),
    ...createFilterByInput<ResourcesFilterableFields>(
      ResourcesFilterableFields.OsSubtype,
      queryState.operatingSystemSubTypes ?? [],
      queryState.excludeOperatingSystems ?? false
    ),
    ...createFilterByInput<ResourcesFilterableFields>(
      ResourcesFilterableFields.Region,
      queryState.regions ?? [],
      queryState.excludeRegions ?? false
    ),
    ...createFilterByInput<ResourcesFilterableFields>(
      ResourcesFilterableFields.Provider,
      queryState.cloudProviders ?? [],
      queryState.excludeCloudProviders ?? false
    ),
    ...createFilterByInput<ResourcesFilterableFields>(
      ResourcesFilterableFields.ProviderId,
      queryState.cloudAccounts ?? [],
      queryState.excludeCloudAccounts ?? false
    ),
    ...createFilterByInput<ResourcesFilterableFields>(
      ResourcesFilterableFields.Tags,
      queryState.resourceTagKeysAndTagValues
        ? [
            transformTagKeysAndValuesToJsonString(
              queryState.resourceTagKeysAndTagValues
            ),
          ]
        : [],
      queryState.excludeResourceTagKeysAndTagValues ?? false
    ),
    ...createFilterByInput<ResourcesFilterableFields>(
      ResourcesFilterableFields.PlanId,
      queryState.plans ?? [],
      queryState.excludePlans ?? false
    ),
    ...createFilterByInput<ResourcesFilterableFields>(
      ResourcesFilterableFields.PingStatus,
      queryState.resourcePingStatuses ?? [],
      queryState.excludeResourcePingStatuses ?? false
    ),
    ...createFilterByInput<ResourcesFilterableFields>(
      ResourcesFilterableFields.ResourceGroupId,
      queryState.resourceGroups ?? [],
      queryState.excludeResourceGroups ?? false
    ),
  ];

  const filters: ResourcesFilterInput[] = [
    ...(queryState.providerIds && queryState.providerIds.length > 0
      ? [
          {
            field: ResourcesFilterableFields.ProviderId,
            operator: FilterComparisonOperator.Eq,
            values: queryState.providerIds.map((id) => String(id)),
          },
        ]
      : []),
    ...(queryState.updateStatus && queryState.updateStatus.length > 0
      ? [
          {
            field: ResourcesFilterableFields.ResourceUpdateStatus,
            operator: !queryState.excludeUpdateStatus
              ? FilterComparisonOperator.Eq
              : FilterComparisonOperator.Ne,
            values: queryState.updateStatus,
          },
        ]
      : []),
  ];

  const retVal = [...sidebarFilters, ...filters];

  return retVal.length > 0 ? retVal : undefined;
}

export function transformTagKeysAndValuesToJsonString(
  resourceTagKeysAndTagValues: string[] | undefined
) {
  if (!resourceTagKeysAndTagValues) {
    return "{}";
  }

  const retVal: Record<string, string[]> = resourceTagKeysAndTagValues.reduce(
    (acc, currentValue) => {
      const splitValue = currentValue.split(";");
      const key = splitValue[0]?.trim();
      const value = splitValue[1]?.trim();
      if (key) {
        if (acc[key]) {
          if (value) {
            // eslint-disable-next-line fp/no-mutating-methods
            acc[key].push(value);
          }
        } else {
          if (value) {
            // eslint-disable-next-line fp/no-mutation
            acc[key] = [value];
          } else {
            // eslint-disable-next-line fp/no-mutation
            acc[key] = [];
          }
        }
      }

      return acc;
    },
    {} as Record<string, string[]>
  );

  return JSON.stringify(retVal);
}
